import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Eye, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";

function fileHref(id) {
  const token = localStorage.getItem("fl_token") || "";
  return `${API}/company-documents/${id}/file?auth=${encodeURIComponent(token)}`;
}

export default function MyCompany() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/company-documents/mine");
      setDocs(data.documents || []);
    } catch {
      toast.error("Couldn't load documents");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const d of docs) (g[d.category] = g[d.category] || []).push(d);
    return g;
  }, [docs]);

  const acknowledge = async (id) => {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, acknowledged: true } : d)));
    try {
      await api.post(`/company-documents/${id}/acknowledge`);
      toast.success("Marked as read");
    } catch {
      toast.error("Couldn't save");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="my-company-page">
      <PageHeader
        eyebrow="My Company"
        title="My Company"
        subtitle="Company policies, health & safety and other documents from your employer."
      />
      {loading ? (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Loading…</div>
      ) : docs.length === 0 ? (
        <EmptyState icon={Building2} title="Nothing here yet" description="Your company hasn't shared any documents with the crew yet. Check back soon." />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} data-testid={`mycompany-category-${cat}`}>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-3">{cat}</div>
              <div className="grid gap-3">
                {list.map((d) => (
                  <div key={d.id} className="hard-card p-4 flex items-center gap-3" data-testid={`mycompany-doc-${d.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-[#FF5F15]/10 text-[#FF5F15] flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-zinc-900 dark:text-white truncate">{d.title}</div>
                      <div className="mt-1">
                        {d.acknowledged ? (
                          <Badge className="bg-green-600 hover:bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Read</Badge>
                        ) : (
                          <Badge variant="secondary">Not read yet</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={fileHref(d.id)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => { if (!d.acknowledged) acknowledge(d.id); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FF5F15] text-white text-sm font-medium active:scale-95 transition-transform"
                        data-testid={`mycompany-view-${d.id}`}
                      >
                        <Eye className="w-4 h-4" /> View
                      </a>
                      {!d.acknowledged && (
                        <Button variant="outline" size="sm" onClick={() => acknowledge(d.id)} data-testid={`mycompany-ack-${d.id}`}>
                          Mark read
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
